import type { ComputedRef } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { Subscription, SubscriptionColumn } from '@entities/subscription'

import { useSidebarPrivileges } from '@entities/viewer'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'
import { ROWS_PER_PAGE_OPTIONS } from '@shared/ui/paginations'

import { useSubscriptionsStore } from './store'

/** The header and the unfolded payments row's `colspan` both read this one list. */
const SUBSCRIPTION_COLUMNS: SubscriptionColumn[] = [
  { key: 'id', label: '#' },
  { key: 'customer', label: 'Customer' },
  { key: 'kid', label: 'Kid' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'auto-renew', label: 'Auto-renew' },
  { key: 'name', label: 'Subscription name' },
  { key: 'provider', label: 'Provider' },
  { key: 'payment-method', label: 'Payment method' },
  { key: 'status', label: 'Subscription status' },
  { key: 'deal-owner', label: 'Deal owner' },
  { key: 'next-invoice', label: 'Next invoice' },
  { key: 'payments', label: 'Payments', isLabelHidden: true },
  { key: 'actions', label: 'Actions', isLabelHidden: true },
]

interface UseTableSubscriptionsOptions {
  /** Off on the customer's own Subscriptions tab. */
  withCustomer?: boolean
}

interface TableSubscriptionsReturn {
  subscriptions: ComputedRef<Subscription[]>
  columns: SubscriptionColumn[]
  isLoading: ComputedRef<boolean>
  hasSubscriptions: ComputedRef<boolean>
  canManageSubscriptions: ComputedRef<boolean>
  canManageOrders: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  perPageOptions: ComputedRef<number[]>
  isExpanded: (subscription: Subscription) => boolean
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

/** The customer's tab keeps the legacy order: the actions menu opens the row, the customer column is dropped. */
const CUSTOMER_TAB_COLUMNS: SubscriptionColumn[] = [
  ...SUBSCRIPTION_COLUMNS.filter(column => column.key === 'actions'),
  ...SUBSCRIPTION_COLUMNS.filter(column => column.key !== 'customer' && column.key !== 'actions'),
]

export const useTableSubscriptions = ({ withCustomer = true }: UseTableSubscriptionsOptions = {}): TableSubscriptionsReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useSubscriptionsStore()
  const { canManageSubscriptions, canManageOrders } = useSidebarPrivileges()

  const subscriptions = computed<Subscription[]>(() => store.getSubscriptions)
  const isLoading = computed<boolean>(() => store.isLoading)
  const hasSubscriptions = computed<boolean>(() => store.hasSubscriptions)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const perPageOptions = computed<number[]>(() => {
    const current = meta.value.per_page
    if (!current || ROWS_PER_PAGE_OPTIONS.includes(current)) return ROWS_PER_PAGE_OPTIONS

    return [...ROWS_PER_PAGE_OPTIONS, current].sort((a, b) => a - b)
  })

  const isExpanded = (subscription: Subscription): boolean => store.getExpandedId === subscription.id

  const setChangePage = async (value: number): Promise<void> => {
    store.setCurrentPage(value)
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    store.setPerPage(value)
    store.setCurrentPage(1)
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
        page: undefined,
      },
    })
  }

  return {
    subscriptions,
    columns: withCustomer ? SUBSCRIPTION_COLUMNS : CUSTOMER_TAB_COLUMNS,
    isLoading,
    hasSubscriptions,
    canManageSubscriptions,
    canManageOrders,
    meta,
    perPageOptions,
    isExpanded,
    setChangePage,
    setChangePerPage,
  }
}
